
import { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox,
  FormControlLabel,
  Switch,
  Snackbar
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { APIHOST } from '../../common';
import { Conference } from '../../types/conference';
import { ConferenceHotelRoom, ConferenceHotelRoomResponse } from '../../types/conferenceHotelRoom';
import { Hotel } from '../../types/hotel';
import { ConferenceGroup, ConferenceGroupResponse } from '../../types/conferenceGroup';
import { ConferenceHotel } from '../../types/conferenceHotel';
import EditConferenceDialog from './EditConferenceDialog';
import EditGroupDialog from './EditGroupDialog';
import EditConferenceHotelDialog from './EditConferenceHotelDialog';
import { EditConferenceHotelRoomDialog } from './EditConferenceHotelRoomDialog';
import {EditConferenceHotelRoomDialogProps} from './EditConferenceHotelRoomDialog';
import HotelAvailabilityTooltip from '../tooltips/HotelAvailabilityTooltip';
import { AvailabilityCalendar } from '../../types/availability';
import { getToken } from '../auth/AuthContext';

interface ConferenceDetailProps {
  conferenceId: string;
}

 interface EditConferenceHotelRoomDialog{
  conferenceHotelId: string | null; // ID of the selected conference hotel
  conferenceHotelRoomId: string | null; // null for adding new room, string for editing existing room
  open: boolean;
}  


export default function ConferenceDetail({ conferenceId }: ConferenceDetailProps) {
  const [conference, setConference] = useState<Conference | null>(null);
  const [rooms, setRooms] = useState<ConferenceHotelRoom[]>([]);
  const [conferenceHotels, setConferenceHotels] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [error, setError] = useState('');
  const [roomsError, setRoomsError] = useState('');
  const [showRooms, setShowRooms] = useState(false);
  const [showAddRoomDialog, setShowAddRoomDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    dailyRate: '',
    maxOccupants: '',
    bedCount: '1',
    conference_hotel: '',
    allocations: {} as Record<string, number>
  });
  const [editingRoom, setEditingRoom] = useState<ConferenceHotelRoom | null>(null);
  const [showAddHotelDialog, setShowAddHotelDialog] = useState(false);
  const [hotelDialogTab, setHotelDialogTab] = useState(0);
  const [availableHotels, setAvailableHotels] = useState<Hotel[]>([]);
  const [selectedHotels, setSelectedHotels] = useState<string[]>([]);
  const [newHotelData, setNewHotelData] = useState({
    name: '',
    longName: '',
    addressCity: '',
    addressState: '',
    addressZip: '',
    website: '',
    amenities: '',
    contactName: '',
    contactEmail: ''
  });



  const [hotelFormLoading, setHotelFormLoading] = useState(false);
  const [hotelFormError, setHotelFormError] = useState('');
  const [showEditConferenceDialog, setShowEditConferenceDialog] = useState(false);
  const [hotelTabs, setHotelTabs] = useState<Record<string, number>>({});
  const [mainTab, setMainTab] = useState(0); // 0: Blocks, 1: Hotels, 2: Groups
  const [conferenceGroups, setConferenceGroups] = useState<ConferenceGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [groupsError, setGroupsError] = useState('');
  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ConferenceGroup | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [showEditConferenceHotelDialog, setShowEditConferenceHotelDialog] = useState(false);
  const [editingConferenceHotel, setEditingConferenceHotel] = useState<ConferenceHotel | null>(null);
  const [editRoomDialog, setEditRoomDialog] = useState<EditConferenceHotelRoomDialog>({open: false, conferenceHotelId: null, conferenceHotelRoomId: null});
  const [conferenceAvailability, setConferenceAvailability] = useState<AvailabilityCalendar | null>(null);


  const handleHotelTabChange = (hotelId: string, newValue: number) => {
    setHotelTabs(prev => ({ ...prev, [hotelId]: newValue }));
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleHotelTierChange = async (hotelId: string, isTierB: boolean) => {
    try {

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Update conference hotel tier via API
      const response = await fetch(`${APIHOST}/api/conference-hotels/${hotelId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            tier: isTierB ? 'B' : 'A'
          }
        }),
      });

      fetchConferenceHotels();

      if (!response.ok) {
        throw new Error('Failed to update hotel tier');
      }

      console.log(`Hotel ${hotelId} tier updated to: ${isTierB ? 'Tier B' : 'Tier A'}`);
      
      // Show success snackbar
      setSnackbarMessage('Hotel tier updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error updating hotel tier:', err);

      // Show error snackbar
      setSnackbarMessage('Hotel tier update failed');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleHotelVisibilityChange = async (hotelId: string, isHidden: boolean) => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Update conference hotel visibility via API
      const response = await fetch(`${APIHOST}/api/conference-hotels/${hotelId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            visible: !isHidden
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update hotel visibility');
      }

      fetchConferenceHotels();

      console.log(`Hotel ${hotelId} visibility updated to: ${isHidden ? 'Hidden' : 'Visible'}`);
      
      // Show success snackbar
      setSnackbarMessage('Hotel visibility updated');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Error updating hotel visibility:', err);

      // Show error snackbar
      setSnackbarMessage('Hotel visibility update failed');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    if (conferenceId) {
      fetchConference();
      fetchConferenceRooms();
      fetchConferenceHotels();
      fetchConferenceGroups();
      fetchConferenceAvailability();
    }
  }, [conferenceId]);


  const fetchConferenceAvailability = async () => {
    try {
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/conferences/${conferenceId}/availability`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch conference availability');
      }

      const data = await response.json();
      setConferenceAvailability(data);

    } catch (err) {
      console.error('Error fetching conference availability:', err);
     
    }
  };

  const fetchConference = async () => {
    try {
      setLoading(true);
      setError('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/conferences/${conferenceId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch conference details');
      }

      const data = await response.json();
      setConference(data.data);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load conference details');
      console.error('Conference detail error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchConferenceRooms = async () => {
    try {
      setRoomsLoading(true);
      setRoomsError('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      let allRooms: any[] = [];
      let currentPage = 1;
      let totalPages = 1;

      // Fetch all pages of conference hotel rooms
      do {
        const response = await fetch(
          `${APIHOST}/api/conference-hotel-rooms?filters[conference_hotel][conference][documentId][$eq]=${conferenceId}&populate=conference_hotel.hotel&sort=name:asc&pagination[page]=${currentPage}&pagination[pageSize]=200`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch conference rooms');
        }

        const data: ConferenceHotelRoomResponse = await response.json();
        
        // Add rooms from this page to our collection
        if (data.data) {
          allRooms = [...allRooms, ...data.data];
        }

        // Update pagination info from first response
        if (currentPage === 1 && data.meta?.pagination) {
          totalPages = data.meta.pagination.pageCount;
        }

        currentPage++;
      } while (currentPage <= totalPages);

      setRooms(allRooms);

    } catch (err) {
      setRoomsError(err instanceof Error ? err.message : 'Failed to load conference rooms');
      console.error('Conference rooms error:', err);
    } finally {
      setRoomsLoading(false);
    }
  };

  const fetchConferenceHotels = async () => {
    try {
      const token = getToken();
      if (!token) return;

      const response = await fetch(
        `${APIHOST}/api/conference-hotels?filters[conference][documentId][$eq]=${conferenceId}&populate=hotel&pagination[pageSize]=100&pagination[page]=1&sort=hotel.longName:asc`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setConferenceHotels(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching conference hotels:', err);
    }
  };

  const fetchConferenceGroups = async () => {
    try {
      setGroupsLoading(true);
      setGroupsError('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(
        `${APIHOST}/api/conference-groups?filters[conference][documentId][$eq]=${conferenceId}&sort=name:asc`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch conference groups');
      }

      const data: ConferenceGroupResponse = await response.json();
      setConferenceGroups(data.data || []);

    } catch (err) {
      setGroupsError(err instanceof Error ? err.message : 'Failed to load conference groups');
      console.error('Error fetching conference groups:', err);
    } finally {
      setGroupsLoading(false);
    }
  };

  const handleCloseConferenceHotelDialog = () => {
    setShowEditConferenceHotelDialog(false);
    setEditingConferenceHotel(null);
  };

  const handleCloseRoomDialog = () => {
    setEditRoomDialog({open: false, conferenceHotelId: null, conferenceHotelRoomId: null});
    fetchConferenceRooms();
  };

  const handleConferenceHotelUpdated = async () => {
    await Promise.all([fetchConferenceHotels(), fetchConferenceRooms()]);
  };

  const handleEditGroup = (group: ConferenceGroup) => {
    setEditingGroup(group);
    setShowAddGroupDialog(true);
  };

  const handleAddGroup = () => {
    setEditingGroup(null);
    setShowAddGroupDialog(true);
  };

  const handleCloseGroupDialog = () => {
    setShowAddGroupDialog(false);
    setEditingGroup(null);
  };

  const handleGroupUpdated = async () => {
    await fetchConferenceGroups();
  };

  const handleAddExistingHotels = async () => {
    try {
      setHotelFormLoading(true);
      setHotelFormError('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (selectedHotels.length === 0) {
        throw new Error('Please select at least one hotel');
      }

      // Create conference-hotel associations for each selected hotel
      const promises = selectedHotels.map(hotelId => 
        fetch(`${APIHOST}/api/conference-hotels`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            data: {
              conference: conferenceId,
              hotel: hotelId
            }
          })
        })
      );

      const responses = await Promise.all(promises);
      const failedRequests = responses.filter(r => !r.ok);
      
      if (failedRequests.length > 0) {
        throw new Error(`Failed to add ${failedRequests.length} hotel(s)`);
      }

      // Reset and refresh
      setSelectedHotels([]);
      setShowAddHotelDialog(false);
      await Promise.all([fetchConferenceHotels(), fetchConferenceRooms()]);

    } catch (err) {
      setHotelFormError(err instanceof Error ? err.message : 'Failed to add hotels');
    } finally {
      setHotelFormLoading(false);
    }
  };

  const handleCreateNewHotel = async () => {
    try {
      setHotelFormLoading(true);
      setHotelFormError('');

      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      if (!newHotelData.name || !newHotelData.longName) {
        throw new Error('Hotel name and full name are required');
      }

      // First create the hotel
      const hotelResponse = await fetch(`${APIHOST}/api/hotels`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            name: newHotelData.name,
            longName: newHotelData.longName,
            addressCity: newHotelData.addressCity || null,
            addressState: newHotelData.addressState || null,
            addressZip: newHotelData.addressZip || null,
            website: newHotelData.website || null,
            amenities: newHotelData.amenities || null,
            contactName: newHotelData.contactName || null,
            contactEmail: newHotelData.contactEmail || null
          }
        })
      });

      if (!hotelResponse.ok) {
        throw new Error('Failed to create hotel');
      }

      const hotelData = await hotelResponse.json();

      // Then associate it with the conference
      const conferenceHotelResponse = await fetch(`${APIHOST}/api/conference-hotels`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            conference: conferenceId,
            hotel: hotelData.data.documentId
          }
        })
      });

      if (!conferenceHotelResponse.ok) {
        throw new Error('Failed to associate hotel with conference');
      }

      // Reset and refresh
      setNewHotelData({
        name: '',
        longName: '',
        addressCity: '',
        addressState: '',
        addressZip: '',
        website: '',
        amenities: '',
        contactName: '',
        contactEmail: ''
      });
      setShowAddHotelDialog(false);
      await Promise.all([fetchConferenceHotels(), fetchConferenceRooms()]);

    } catch (err) {
      setHotelFormError(err instanceof Error ? err.message : 'Failed to create hotel');
    } finally {
      setHotelFormLoading(false);
    }
  };

  const handleCloseHotelDialog = () => {
    setShowAddHotelDialog(false);
    setHotelDialogTab(0);
    setHotelFormError('');
    setSelectedHotels([]);
    setNewHotelData({
      name: '',
      longName: '',
      addressCity: '',
      addressState: '',
      addressZip: '',
      website: '',
      amenities: '',
      contactName: '',
      contactEmail: ''
    });
  };

  const handleEditConference = () => {
    setShowEditConferenceDialog(true);
  };

  const handleCloseEditConferenceDialog = () => {
    setShowEditConferenceDialog(false);
  };

  const handleConferenceUpdated = async () => {
    await fetchConference();
  };

  const handleEditRoom = (room: ConferenceHotelRoom) => {
    setEditRoomDialog({open: true, conferenceHotelId: room.conference_hotel.documentId, conferenceHotelRoomId: room.documentId});
  };
  const handleAddRoom = (conferenceHotelId: string) => {
    setEditRoomDialog({open: true, conferenceHotelId, conferenceHotelRoomId: null});
  }

  const handleDeleteRoom = async (room: ConferenceHotelRoom) => {
    if (!window.confirm(`Are you sure you want to delete "${room.name}"? This will hide the room from availability.`)) {
      return;
    }

    try {
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${APIHOST}/api/conference-hotel-rooms/${room.documentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: {
            visibility: 'hidden'
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete room');
      }

      // Show success message
      setSnackbarMessage('Room deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Refresh rooms list
      await fetchConferenceRooms();

    } catch (err) {
      console.error('Error deleting room:', err);
      setSnackbarMessage('Failed to delete room. Please try again.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  // Generate date range when conference hotel changes
  const generateDateRange = (conferenceHotelId: string): Record<string, number> => {
    const selectedHotel = conferenceHotels.find(hotel => hotel.documentId === conferenceHotelId);
    if (!selectedHotel) return {};

    const startDate = new Date(selectedHotel.earliestAvailable);
    const endDate = new Date(selectedHotel.latestAvailable);
    const allocations: Record<string, number> = {};

    // Generate all dates in range
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      allocations[d.toISOString().split('T')[0]] = 0; // yyyy-mm-dd format
    }

    return allocations;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString+`T00:00:00`).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getConferenceStatus = (startDate: string, endDate: string) => {
    const now = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (now < start) return { label: 'Upcoming', color: 'primary' as const };
    if (now >= start && now <= end) return { label: 'Active', color: 'success' as const };
    return { label: 'Completed', color: 'default' as const };
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body1">Loading conference details...</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!conference) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="warning">Conference not found</Alert>
      </Paper>
    );
  }

  const status = getConferenceStatus(conference.defaultArrivalDate, conference.defaultDepartureDate);

  return (
    <Box>
      {/* Header Section with Conference Details */}
      <Paper sx={{ p: 4, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          {conference.logo && (
            <Avatar
              src={conference.logo.url}
              alt={conference.logo.alternativeText || conference.longName}
              sx={{ width: 80, height: 80 }}
            />
          )}
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {conference.longName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleEditConference}
                >
                  Edit
                </Button>
                <Chip 
                  label={status.label}
                  color={status.color}
                  size="medium"
                />
              </Box>
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {conference.name}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            {/* Conference Details moved to header */}
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Conference Dates
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(conference.defaultArrivalDate)} - {formatDate(conference.defaultDepartureDate)}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  Earliest Arrival
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {formatDate(conference.earliestArrivalDate)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>

      {/* Logo Information */}
      {conference.logo && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Conference Logo
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2} alignItems="center">
                  <Grid>
                    <Avatar
                      src={conference.logo.url}
                      alt={conference.logo.alternativeText || conference.longName}
                      sx={{ width: 120, height: 120 }}
                      variant="rounded"
                    />
                  </Grid>
                  <Grid>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        File Name
                      </Typography>
                      <Typography variant="body1">
                        {conference.logo.name}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Dimensions
                      </Typography>
                      <Typography variant="body1">
                        {conference.logo.width} × {conference.logo.height} pixels
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        File Size
                      </Typography>
                      <Typography variant="body1">
                        {(conference.logo.size / 1024).toFixed(1)} KB
                      </Typography>
                    </Box>

                    {conference.logo.alternativeText && (
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Alt Text
                        </Typography>
                        <Typography variant="body1">
                          {conference.logo.alternativeText}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Conference Hotels/Groups/Blocks Section */}
      <Paper sx={{ p: 3, mt:  3}}>

        <Tabs value={mainTab} onChange={(e, newValue) => setMainTab(newValue)} sx={{ mb: 3 }}>
          <Tab label="Hotels" />
          <Tab label="Groups" />
        </Tabs>

        {/* Hotels Tab */}
        {mainTab === 0 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    setEditingConferenceHotel(null);
                    setShowEditConferenceHotelDialog(true);
                  }}
                >
                  Add Hotel to Conference
                </Button>
              </Box>
            </Box>

        {roomsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading rooms...
            </Typography>
          </Box>
        )}

        {roomsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {roomsError}
          </Alert>
        )}

        {conferenceHotels.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No hotels associated with this conference
          </Typography>
        ) : (
          <Box>
            {conferenceHotels.map((conferenceHotel) => {
              // Get rooms for this specific hotel
              const hotelRooms = rooms.filter(room => 
                room.conference_hotel?.documentId === conferenceHotel.documentId
              );

              return (
                <Card key={conferenceHotel.documentId} sx={{ m: 3 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {conferenceHotel.hotel.longName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row' }}>
                          <Typography variant="body2" color="text.secondary">
                            Tier A
                          </Typography>
                          <Switch
                            checked={conferenceHotel.tier === 'B'}
                            onChange={(e) => handleHotelTierChange(conferenceHotel.documentId, e.target.checked)}
                            color={"default"}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Tier B
                          </Typography>
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row' }}>
                          <Typography variant="body2" color="text.secondary">
                            Visible
                          </Typography>
                          <Switch
                            checked={conferenceHotel.visible === false}
                            onChange={(e) => handleHotelVisibilityChange(conferenceHotel.documentId, e.target.checked)}
                            color={conferenceHotel.visible === false ? "error" : "default"}
                          />
                          <Typography variant="body2" color="text.secondary">
                            Hidden
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Hotel Tabs */}
                    <Tabs 
                      value={hotelTabs[conferenceHotel.documentId] || 0} 
                      onChange={(e, newValue) => handleHotelTabChange(conferenceHotel.documentId, newValue)}
                      sx={{ mb: 3 }}
                    >
                      <Tab label="Rooms" />
                      <Tab label="Details" />
                    </Tabs>

                    {/* Rooms Tab Content */}
                    {(hotelTabs[conferenceHotel.documentId] || 0) === 0 && (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6" sx={{ color: 'primary.main' }}>
                            Available Rooms ({hotelRooms.length})
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => {
                              handleAddRoom(conferenceHotel.documentId);
                            }}
                          >
                            Add Room Type
                          </Button>
                        </Box>
                    
                    {!roomsLoading && hotelRooms.length === 0 && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No rooms available for this hotel
                      </Typography>
                    )}
                    
                    {hotelRooms.length > 0 && (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Room Name</strong></TableCell>
                              <TableCell align="right"><strong>Daily Rate</strong></TableCell>
                              <TableCell align="center"><strong>Availability</strong></TableCell>
                              <TableCell align="center"><strong>Max Occupants</strong></TableCell>
                              <TableCell align="center"><strong>Beds</strong></TableCell>
                              <TableCell align="center"><strong>Images</strong></TableCell>
                              <TableCell align="center"><strong>Orders</strong></TableCell>
                              <TableCell align="center"><strong>Actions</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {hotelRooms.sort((a, b) => {
                              if (a.visibility === 'hidden' && b.visibility !== 'hidden') return 1;
                              if (a.visibility !== 'hidden' && b.visibility === 'hidden') return -1;
                              return a.name.localeCompare(b.name);
                            }).map((room: ConferenceHotelRoom) => (
                              <TableRow key={room.documentId} hover>
                                <TableCell>
                                  <Typography variant="body2">
                                    {room.name}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  {formatCurrency(room.dailyRate)}
                                  
                                </TableCell>

                                <TableCell align="center">
                                  ?
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2">
                                    {room.maxOccupants || 'N/A'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Typography variant="body2">
                                    {room.bedCount || '?'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  {room.pics && room.pics.length > 0 ? (
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                      {room.pics.slice(0, 3).map((pic, index) => (
                                        <Avatar
                                          key={pic.id}
                                          src={pic.url}
                                          alt={pic.alternativeText || room.name}
                                          sx={{ width: 32, height: 32 }}
                                          variant="rounded"
                                        />
                                      ))}
                                      {room.pics.length > 3 && (
                                        <Chip
                                          label={`+${room.pics.length - 3}`}
                                          size="small"
                                          sx={{ height: 32, fontSize: '0.7rem' }}
                                        />
                                      )}
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary">
                                      No images
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    href={`/admin/orders?filters[conference_hotel_room]=${room.documentId}&message=Viewing orders for ${conference.longName} > ${conferenceHotel.hotel.longName} > ${room.name}`}
                                    title="View Orders for this Room"
                                  >
                                    <SearchIcon />
                                  </IconButton>
                                </TableCell>
                                <TableCell align="center">
                                  {room.visibility === 'visible' && <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleEditRoom(room)}
                                      title="Edit Room"
                                    >
                                      <EditIcon />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteRoom(room)}
                                      title="Delete Room"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Box>}
                                  {room.visibility === 'hidden' && <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                      Deleted
                                    </Typography>
                                  </Box>}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                      </Box>
                    )}

                    {/* Details Tab Content */}
                    {(hotelTabs[conferenceHotel.documentId] || 0) === 1 && (
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                          <Typography variant="h6" sx={{ color: 'primary.main' }}>
                            Hotel Configuration
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setEditingConferenceHotel(conferenceHotel);
                              setShowEditConferenceHotelDialog(true);
                            }}
                          >
                            Edit Hotel Details
                          </Button>
                        </Box>
                        
                        <Grid container spacing={3}>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Transportation & Logistics
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Bus Parking Available
                              </Typography>
                              <Chip 
                                label={conferenceHotel.hasBusParking ? 'Yes' : 'No'}
                                color={conferenceHotel.hasBusParking ? 'success' : 'default'}
                                size="small"
                              />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Transit to Venue Available
                              </Typography>
                              <Chip 
                                label={conferenceHotel.hasTransitToVenue ? 'Yes' : 'No'}
                                color={conferenceHotel.hasTransitToVenue ? 'success' : 'default'}
                                size="small"
                              />
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Credit Card Required
                              </Typography>
                              <Chip 
                                label={conferenceHotel.requiresCreditCard ? 'Yes' : 'No'}
                                color={conferenceHotel.requiresCreditCard ? 'warning' : 'default'}
                                size="small"
                              />
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="subtitle1" gutterBottom>
                              Tax Information
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Tax Per Room/Night
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.taxPerRoomNight ? formatCurrency(conferenceHotel.taxPerRoomNight) : 'Not set'}
                              </Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Tax Per Room/Night (Non-Profit)
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.taxPerRoomNightNonProfit ? formatCurrency(conferenceHotel.taxPerRoomNightNonProfit) : 'Not set'}
                              </Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Tax Rate Percentage
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.taxRatePercentage ? `${conferenceHotel.taxRatePercentage}%` : 'Not set'}
                              </Typography>
                            </Box>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Tax Rate Percentage (Non-Profit)
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.taxRatePercentageNonProfit ? `${conferenceHotel.taxRatePercentageNonProfit}%` : 'Not set'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle1" gutterBottom>
                              Contact & Administrative
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Contact Name
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.contactName || 'Not provided'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Contact Email
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.contactEmail || 'Not provided'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12, md: 4 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Contact Phone
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.contactPhone || 'Not provided'}
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                CSV File Send Date
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.dateToSendCSVFile 
                                  ? formatDate(conferenceHotel.dateToSendCSVFile) 
                                  : 'Not scheduled'
                                }
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle1" gutterBottom>
                              Availability <HotelAvailabilityTooltip />
                            </Typography>
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Earliest Available Date
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.earliestAvailable 
                                  ? formatDate(conferenceHotel.earliestAvailable) 
                                  : 'Not set'
                                }
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12, md: 6 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Latest Available Date
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.latestAvailable 
                                  ? formatDate(conferenceHotel.latestAvailable) 
                                  : 'Not set'
                                }
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid size={{ xs: 12 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="body2" color="text.secondary">
                                Minimum nights stay requirement
                              </Typography>
                              <Typography variant="body1">
                                {conferenceHotel.minNights ? `${conferenceHotel.minNights} night(s)` : 'No minimum'}
                              </Typography>
                            </Box>
                          </Grid>

                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
          </Box>
        )}

        {/* Groups Tab */}
        {mainTab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Conference Groups
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddGroup}
              >
                Add Group
              </Button>
            </Box>

            {groupsLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ ml: 2 }}>
                  Loading groups...
                </Typography>
              </Box>
            )}

            {groupsError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {groupsError}
              </Alert>
            )}

            {!groupsLoading && conferenceGroups.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                No groups configured for this conference
              </Typography>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Group Name</strong></TableCell>
                      <TableCell align="center"><strong>Access Code</strong></TableCell>
                      <TableCell align="center"><strong>Logins After</strong></TableCell>
                      <TableCell align="center"><strong>A-Tier Allowed</strong></TableCell>
                      <TableCell align="center"><strong>A-Tier Reserved</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conferenceGroups.map((group) => (
                      <TableRow key={group.documentId} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body1" fontWeight="medium">
                              {group.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {group.longName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={group.accessCode}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {group.allowLoginsAfterDateTime 
                              ? new Date(group.allowLoginsAfterDateTime).toLocaleString()
                              : 'No restriction'
                            }
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={group.aTierAllowed}
                            size="small"
                            color={group.aTierAllowed ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                            {group.aTierReserved || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleEditGroup(group)}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Paper>

      {/* Add/Edit Group Dialog */}
      <EditGroupDialog
        open={showAddGroupDialog}
        onClose={handleCloseGroupDialog}
        conferenceId={conferenceId}
        group={editingGroup}
        onUpdate={handleGroupUpdated}
      />

      {/* Manage Hotels Dialog */}
      <Dialog 
        open={showAddHotelDialog} 
        onClose={handleCloseHotelDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Manage Conference Hotels</DialogTitle>
        <DialogContent>
          {hotelFormError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {hotelFormError}
            </Alert>
          )}
          
          <Tabs value={hotelDialogTab} onChange={(e, newValue) => setHotelDialogTab(newValue)} sx={{ mb: 3 }}>
            <Tab label="Add Existing Hotels" />
            <Tab label="Create New Hotel" />
          </Tabs>

          {hotelDialogTab === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select existing hotels to add to this conference:
              </Typography>
              
              {availableHotels.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                  No available hotels found or all hotels are already associated with this conference
                </Typography>
              ) : (
                <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {availableHotels.map((hotel) => (
                    <ListItem key={hotel.documentId} disablePadding>
                      <ListItemButton 
                        onClick={() => {
                          setSelectedHotels(prev => 
                            prev.includes(hotel.documentId)
                              ? prev.filter(id => id !== hotel.documentId)
                              : [...prev, hotel.documentId]
                          );
                        }}
                      >
                        <Checkbox
                          checked={selectedHotels.includes(hotel.documentId)}
                          sx={{ ml: 2 }}
                        />
                        <ListItemText
                          primary={hotel.longName}
                          secondary={[
                            hotel.addressCity,
                            hotel.addressState
                          ].filter(Boolean).join(', ')}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}

          {hotelDialogTab === 1 && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Hotel Name (Short)"
                    value={newHotelData.name}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, name: e.target.value }))}
                    fullWidth
                    required
                    placeholder="e.g., Marriott Downtown"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Hotel Name (Full)"
                    value={newHotelData.longName}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, longName: e.target.value }))}
                    fullWidth
                    required
                    placeholder="e.g., Downtown Marriott Hotel & Convention Center"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="City"
                    value={newHotelData.addressCity}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, addressCity: e.target.value }))}
                    fullWidth
                    placeholder="e.g., San Francisco"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="State"
                    value={newHotelData.addressState}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, addressState: e.target.value }))}
                    fullWidth
                    placeholder="e.g., CA"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="ZIP Code"
                    value={newHotelData.addressZip}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, addressZip: e.target.value }))}
                    fullWidth
                    placeholder="e.g., 94102"
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Website"
                    value={newHotelData.website}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, website: e.target.value }))}
                    fullWidth
                    placeholder="https://www.hotel-website.com"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Contact Name"
                    value={newHotelData.contactName}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, contactName: e.target.value }))}
                    fullWidth
                    placeholder="e.g., John Smith"
                  />
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Contact Email"
                    type="email"
                    value={newHotelData.contactEmail}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, contactEmail: e.target.value }))}
                    fullWidth
                    placeholder="contact@hotel.com"
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Amenities"
                    value={newHotelData.amenities}
                    onChange={(e) => setNewHotelData(prev => ({ ...prev, amenities: e.target.value }))}
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="e.g., Pool, Fitness Center, Business Center, Free WiFi"
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHotelDialog} disabled={hotelFormLoading}>
            Cancel
          </Button>
          {hotelDialogTab === 0 ? (
            <Button 
              onClick={handleAddExistingHotels} 
              variant="contained" 
              disabled={hotelFormLoading || selectedHotels.length === 0}
            >
              {hotelFormLoading ? <CircularProgress size={20} /> : `Add Selected Hotels (${selectedHotels.length})`}
            </Button>
          ) : (
            <Button 
              onClick={handleCreateNewHotel} 
              variant="contained" 
              disabled={hotelFormLoading || !newHotelData.name || !newHotelData.longName}
            >
              {hotelFormLoading ? <CircularProgress size={20} /> : 'Create & Add Hotel'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Conference Dialog */}
      <EditConferenceDialog
        open={showEditConferenceDialog}
        onClose={handleCloseEditConferenceDialog}
        conference={conference}
        onUpdate={handleConferenceUpdated}
      />

      {/* Edit Conference Hotel Dialog */}
      {conference && (
        <EditConferenceHotelDialog
          open={showEditConferenceHotelDialog}
          onClose={handleCloseConferenceHotelDialog}
          conference={conference}
          conferenceHotel={editingConferenceHotel}
          onUpdate={handleConferenceHotelUpdated}
        />
      )}

      {/* Edit Room Dialog */}
      {editRoomDialog.open && <EditConferenceHotelRoomDialog
          conferenceHotelId={editRoomDialog.conferenceHotelId}
          conferenceHotelRoomId={editRoomDialog.conferenceHotelRoomId}
          handleCloseDialog={handleCloseRoomDialog}
          open={editRoomDialog.open}
      />}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}